import type { WebsiteSyncMode } from '@kanbu/schema/contracts';
import { SourceType } from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Badge, Button, Dropdown, toast } from '@utima/ui';
import { AlertTriangle, ChevronDown, RefreshCw } from 'lucide-react';
import { memo } from 'react';

import { AutoUpdatePausedBadge } from '@/components';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';

import type { KBFolderData } from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';
import { useSourceTypeLabels } from '../hooks/useSourceTypeLabels';
import { isManagedSourceRoot } from '../lib/managedSource';

export type SourceStatusHeaderProps = {
  folder: KBFolderData | null | undefined;
  mutations: ReturnType<typeof useKBMutations>;
};

function toDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value as string);

  return Number.isNaN(date.getTime()) ? null : date;
}

type DriveSyncResult = {
  filesCreated: number;
  filesSkipped: number;
  filesFailed: number;
};

function isDriveSyncResult(value: unknown): value is DriveSyncResult {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.filesCreated === 'number' &&
    typeof candidate.filesSkipped === 'number' &&
    typeof candidate.filesFailed === 'number'
  );
}

/**
 * Connected-source treatment for a managed source root (master §3.2/§3.3):
 * origin badge, sync schedule, error state and a manual sync trigger.
 */
export const SourceStatusHeader = memo(function SourceStatusHeader({
  folder,
  mutations,
}: SourceStatusHeaderProps) {
  const { t } = useLingui();
  const sourceTypeLabels = useSourceTypeLabels();
  const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();

  if (!folder || !isManagedSourceRoot(folder)) {
    return null;
  }

  const sourceType = (folder.sourceType as SourceType) ?? SourceType.Upload;
  const isWebsite = sourceType === SourceType.Website;
  const meta =
    sourceTypeLabels[sourceType] ?? sourceTypeLabels[SourceType.Upload];
  const SourceIcon = meta.icon;
  const identity = folder.sourceUri ?? null;
  const lastSyncError = folder.lastSyncError as string | null | undefined;
  const lastSyncedAt = toDate(folder.lastSyncedAt);
  const nextUpdateAt = toDate(folder.nextUpdateAt);
  const autoUpdatePaused = isKbAutoUpdatesLocked && !!nextUpdateAt;

  const handleSyncNow = async (mode: WebsiteSyncMode = 'crawl') => {
    try {
      const result = await mutations.triggerCrawl.mutateAsync({
        id: folder.id,
        ...(isWebsite ? { mode } : {}),
      });

      if (isDriveSyncResult(result)) {
        const { filesCreated, filesSkipped, filesFailed } = result;
        const message = t`Drive sync completed. Imported: ${filesCreated}; skipped: ${filesSkipped}; failed: ${filesFailed}. Imported files are queued for processing.`;

        if (filesFailed > 0 || filesSkipped > 0) {
          toast.warning(message, { duration: 6000 });
        } else {
          toast.success(message, { duration: 6000 });
        }

        return;
      }

      toast.success(
        isWebsite
          ? mode === 'crawl_and_refresh'
            ? t`Crawl and refresh queued. We will check for new, removed and changed content in the background. Changes may take up to 20 minutes to appear.`
            : t`Crawl queued. We will check for new and removed pages in the background. Changes may take up to 20 minutes to appear.`
          : t`Sync started. New content will appear shortly — refresh to check for updates.`,
        { duration: 6000 },
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : isWebsite
            ? t`Failed to start scan`
            : t`Failed to start sync`,
      );
    }
  };

  return (
    <div className='mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2'>
      <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
        <Badge variant='secondary' size='sm' className='gap-1'>
          <SourceIcon className='size-3' />
          {meta.label}
        </Badge>

        {identity && (
          <span className='truncate text-sm text-muted-fg' title={identity}>
            {identity}
          </span>
        )}

        {lastSyncedAt && (
          <span className='text-xs text-muted-fg'>
            {isWebsite ? (
              <Trans>
                Last scanned:{' '}
                {formatUtils.date(lastSyncedAt, DateFormat.DateTimeNumeric)}
              </Trans>
            ) : (
              <Trans>
                Last synced:{' '}
                {formatUtils.date(lastSyncedAt, DateFormat.DateTimeNumeric)}
              </Trans>
            )}
          </span>
        )}

        {nextUpdateAt && !lastSyncError && !autoUpdatePaused && (
          <span className='text-xs text-muted-fg'>
            {isWebsite ? (
              <Trans>
                Next scan:{' '}
                {formatUtils.date(nextUpdateAt, DateFormat.DateTimeNumeric)}
              </Trans>
            ) : (
              <Trans>
                Next update:{' '}
                {formatUtils.date(nextUpdateAt, DateFormat.DateTimeNumeric)}
              </Trans>
            )}
          </span>
        )}

        <AutoUpdatePausedBadge paused={autoUpdatePaused} />

        {lastSyncError && (
          <SimpleTooltip title={lastSyncError}>
            <Badge variant='danger' size='sm' className='gap-1'>
              <AlertTriangle className='size-3' />
              {isWebsite ? (
                <Trans>Scan failed</Trans>
              ) : (
                <Trans>Sync failed</Trans>
              )}
            </Badge>
          </SimpleTooltip>
        )}
      </div>

      {isWebsite ? (
        <Dropdown.Root modal={false}>
          <Dropdown.Trigger asChild>
            <Button
              variant='ghost'
              size='sm'
              outline
              loading={mutations.triggerCrawl.isPending}
              disabled={mutations.triggerCrawl.isPending}
              icon={<RefreshCw className='size-4' />}
            >
              <Trans>Crawl</Trans>
              <ChevronDown className='ml-1 size-3' />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Content side='bottom' align='end'>
            <SimpleTooltip
              title={t`Find new and removed pages without checking existing page content. Automatically included new pages use processing credits. Runs in the background; changes may take up to 20 minutes to appear.`}
            >
              <Dropdown.Item onClick={() => handleSyncNow('crawl')}>
                <Trans>Crawl</Trans>
              </Dropdown.Item>
            </SimpleTooltip>
            <SimpleTooltip
              title={t`Also check included pages for content changes and refresh them when the change threshold is exceeded. This may use more processing credits. Runs in the background; changes may take up to 20 minutes to appear.`}
            >
              <Dropdown.Item onClick={() => handleSyncNow('crawl_and_refresh')}>
                <Trans>Crawl and refresh</Trans>
              </Dropdown.Item>
            </SimpleTooltip>
          </Dropdown.Content>
        </Dropdown.Root>
      ) : (
        <Button
          variant='ghost'
          size='sm'
          outline
          onClick={() => handleSyncNow()}
          loading={mutations.triggerCrawl.isPending}
          disabled={mutations.triggerCrawl.isPending}
          icon={<RefreshCw className='size-4' />}
        >
          <Trans>Sync now</Trans>
        </Button>
      )}
    </div>
  );
});
